export type LabelGlyph = { text: string; width: number };
export type PlacedLabelGlyph = LabelGlyph & {
  x: number;
  y: number;
  angle: number;
};

/** Layout in pixels around the label anchor; angles are in radians. */
export function layoutMapLabel(
  lines: readonly (readonly LabelGlyph[])[],
  fontPx: number,
  curve: number,
  spacing: number,
  rotation: number,
): {
  glyphs: PlacedLabelGlyph[];
  minX: number;
  minY: number;
  width: number;
  height: number;
} {
  const bend = (Math.max(-180, Math.min(180, curve)) * Math.PI) / 180;
  const glyphs: PlacedLabelGlyph[] = [];
  lines.forEach((line, row) => {
    const width =
      line.reduce((sum, glyph) => sum + glyph.width, 0) +
      Math.max(0, line.length - 1) * spacing;
    const radius = bend === 0 ? 0 : width / bend;
    let distance = -width / 2;
    for (const glyph of line) {
      const center = distance + glyph.width / 2;
      const angle = radius === 0 ? 0 : center / radius;
      glyphs.push({
        ...glyph,
        angle,
        x: radius === 0 ? center : radius * Math.sin(angle),
        y:
          (row - (lines.length - 1) / 2) * fontPx * 1.2 +
          (radius === 0 ? 0 : radius * (1 - Math.cos(angle))),
      });
      distance += glyph.width + spacing;
    }
  });
  const bounds = (
    placed: readonly PlacedLabelGlyph[],
  ): { minX: number; minY: number; width: number; height: number } => {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const glyph of placed) {
      const cos = Math.cos(glyph.angle),
        sin = Math.sin(glyph.angle);
      for (const x of [-glyph.width / 2, glyph.width / 2]) {
        for (const y of [-fontPx * 0.65, fontPx * 0.65]) {
          const px = glyph.x + x * cos - y * sin;
          const py = glyph.y + x * sin + y * cos;
          minX = Math.min(minX, px);
          maxX = Math.max(maxX, px);
          minY = Math.min(minY, py);
          maxY = Math.max(maxY, py);
        }
      }
    }
    return placed.length === 0
      ? { minX: 0, minY: -fontPx / 2, width: 0, height: fontPx }
      : { minX, minY, width: maxX - minX, height: maxY - minY };
  };
  const local = bounds(glyphs);
  const centerX = local.minX + local.width / 2;
  const centerY = local.minY + local.height / 2;
  const angle = (rotation * Math.PI) / 180;
  const cos = Math.cos(angle),
    sin = Math.sin(angle);
  const placed = glyphs.map((glyph) => {
    const x = glyph.x - centerX,
      y = glyph.y - centerY;
    return {
      ...glyph,
      x: x * cos - y * sin,
      y: x * sin + y * cos,
      angle: glyph.angle + angle,
    };
  });
  return { glyphs: placed, ...bounds(placed) };
}
